import { Loader } from '@transferwise/components';
import { Cross as CrossIcon } from '@transferwise/icons';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import autoBind from 'react-autobind';
import { FormattedMessage, injectIntl } from 'react-intl';

import fetchProvidersDisclaimer from '../resources/clients/quotes/fetchProvidersDisclaimer';
import { dateFormat } from '../resources/time';

import injectTheme from './injectTheme';

const tagValues = {
  b: (...chunks) => <b>{chunks}</b>,
  p: (...chunks) => <p>{chunks}</p>,
  ul: (...chunks) => <ul>{chunks}</ul>,
};

const TEXT_ALIGN_CLASS = 'text-xs-center';

const DisclaimerCloseButton = ({ id, className, createClassNames, onClickToClose }) => {
  return (
    <button type="button" id={id} className={className} onClick={onClickToClose}>
      <CrossIcon size={16} className={createClassNames(['close-disclaimer-icon'])} />
      <FormattedMessage id="close" />
    </button>
  );
};

class Disclaimer extends Component {
  constructor(props) {
    super(props);
    autoBind(this);
    const { expandDisclaimer } = this.props;
    this.state = {
      shouldScroll: expandDisclaimer,
      openDisclaimer: expandDisclaimer,
      formatLocale: null,
      disclaimerData: [],
      isLoading: false,
      error: null,
    };
  }

  componentDidMount() {
    this.fetchDisclaimerData();
  }

  componentDidUpdate(previousProps) {
    const { shouldScroll } = this.state;
    const { expandDisclaimer, sourceCurrency, targetCurrency } = this.props;
    const scroll = shouldScroll && shouldScroll === expandDisclaimer;
    const element = document.querySelector('#close-message');
    if (element && scroll) {
      this.doScroll(element);
    }

    if (
      previousProps.sourceCurrency !== sourceCurrency ||
      previousProps.targetCurrency !== targetCurrency
    ) {
      this.fetchDisclaimerData();
    }
  }

  doScroll(element) {
    element.scrollIntoView();
    this.setState({ shouldScroll: false });
  }

  clear(event) {
    event.preventDefault();
    this.setState({ openDisclaimer: false });
  }

  openDisclaimer(event) {
    const { onClickToSeeDisclaimer } = this.props;
    event.preventDefault();
    this.setState({ openDisclaimer: true });
    onClickToSeeDisclaimer();
  }

  fetchDisclaimerData() {
    const { sourceCurrency, targetCurrency } = this.props;

    if (!sourceCurrency || !targetCurrency) {
      return;
    }

    this.setState({ isLoading: true, error: null });

    fetchProvidersDisclaimer(sourceCurrency, targetCurrency)
      .then((data) => {
        this.setState({
          disclaimerData: data,
          isLoading: false,
        });
      })
      .catch((error) => {
        this.setState({
          error: error,
          isLoading: false,
        });
      });
  }

  renderTable() {
    const { createClassNames, intl } = this.props;
    const { disclaimerData } = this.state;
    const currentLocale = intl.locale;
    return (
      <div className={createClassNames(['table-responsive'])}>
        <table className={createClassNames(['table', 'table-condensed', 'table-striped'])}>
          <thead>
            <tr>
              <td>
                <FormattedMessage id="provider" />
              </td>
              <td>
                <FormattedMessage id="currency-route" />
              </td>
              <td>
                <FormattedMessage id="date-collection" />
              </td>
            </tr>
          </thead>
          <tbody>
            {disclaimerData.map((provider) => (
              <tr key={`${provider.providerName}`}>
                <td>{provider.providerName}</td>
                <td>
                  {provider.sourceCurrency} <FormattedMessage id="to" /> {provider.targetCurrency}
                </td>
                <td>
                  <span>{dateFormat(new Date(provider.dateCollected), currentLocale)} UTC</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  renderDisclaimerText() {
    const { createClassNames, style } = this.props;
    const { isLoading, error, disclaimerData } = this.state;
    if (isLoading) {
      return (
        <div className={createClassNames([TEXT_ALIGN_CLASS, 'm-t-3'])}>
          <Loader classNames={style} />
        </div>
      );
    }

    if (error) {
      return (
        <div className={createClassNames(['small', 'm-t-2'])}>
          <FormattedMessage id="disclaimer-data-error" />
        </div>
      );
    }

    return (
      <div className={createClassNames(['small', 'm-t-2'])}>
        {disclaimerData && (
          <>
            <FormattedMessage values={tagValues} id="disclaimer-introduction" />
            <FormattedMessage values={tagValues} id="disclaimer-transfer-fee" />
            <FormattedMessage values={tagValues} id="disclaimer-exchange-rate" />
            <FormattedMessage values={tagValues} id="disclaimer-amount-not-collected" />
            {disclaimerData.length > 0 && (
              <FormattedMessage values={tagValues} id="disclaimer-conclusion" />
            )}
            {disclaimerData.length > 0 && this.renderTable()}
          </>
        )}
      </div>
    );
  }

  render() {
    const { createClassNames, isScreenModeDark, customFooterDisclaimer } = this.props;
    const { openDisclaimer } = this.state;
    return (
      <div className={`${createClassNames(['container'])} m-t-5`}>
        {openDisclaimer ? (
          <div className={createClassNames(['small', TEXT_ALIGN_CLASS])}>
            <DisclaimerCloseButton
              id="top-close-disclaimer"
              className={createClassNames(['anchor', 'p-t-4', 'disclaimer-close-btn'])}
              createClassNames={createClassNames}
              onClickToClose={this.clear}
            />
          </div>
        ) : (
          <div
            className={createClassNames([
              'small',
              TEXT_ALIGN_CLASS,
              'm-b-5',
              isScreenModeDark ? 'text-content-secondary' : '',
            ])}
          >
            {customFooterDisclaimer || (
              <span data-testid="default-disclaimer">
                <FormattedMessage id="disclaimer-pay-in" />
                &nbsp;
                <button
                  type="button"
                  className={createClassNames(['anchor', isScreenModeDark ? 'anchor--dark' : ''])}
                  onClick={this.openDisclaimer}
                >
                  <FormattedMessage id="how" />
                </button>
              </span>
            )}
          </div>
        )}
        <div className={createClassNames(['col-sm-10', 'col-sm-offset-1', 'm-b-3'])}>
          {openDisclaimer && this.renderDisclaimerText()}
        </div>
        {openDisclaimer && (
          <div className={createClassNames(['small', TEXT_ALIGN_CLASS])}>
            <DisclaimerCloseButton
              id="bottom-close-disclaimer"
              className={createClassNames(['anchor', 'p-b-4', 'disclaimer-close-btn'])}
              createClassNames={createClassNames}
              onClickToClose={this.clear}
            />
          </div>
        )}
      </div>
    );
  }
}

Disclaimer.propTypes = {
  style: PropTypes.shape({
    // TODO
  }).isRequired,
  createClassNames: PropTypes.func.isRequired,
  expandDisclaimer: PropTypes.bool,
  onClickToSeeDisclaimer: PropTypes.func,
  customFooterDisclaimer: PropTypes.element,
  isScreenModeDark: PropTypes.bool,
  sourceCurrency: PropTypes.string,
  targetCurrency: PropTypes.string,
};

Disclaimer.defaultProps = {
  expandDisclaimer: false,
  onClickToSeeDisclaimer: () => {},
};

export default injectTheme(injectIntl(Disclaimer));
